import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Section from '../components/Section';
import { API, store } from '../api';
import { IconCheck, IconShield, IconLock, IconCreditCard, IconRefresh, IconArrowLeft } from '../components/Icon';

/**
 * /payments — checkout page. Shows itemized receipt, one-shot server-minted
 * token, and a payment-method picker (Card · OnePay · Alipay). No PayHere
 * branding anywhere — the backend chooses the processor for the selected
 * method. Read plan from ?plan=…&coupon=… (Account portal navigates here
 * instead of doing an inline paymentIntent → immediate redirect). The token
 * is displayed like a receipt reference so the user has proof of intent
 * before they leave for the processor.
 */

type Method = 'payhere';

const PLAN_META: Record<string, { label: string; price: number; kind: string }> = {
  gold_annual:     { label: 'Gold',     price: 20.99, kind: 'Annual' },
  platinum_annual: { label: 'Platinum', price: 31.99, kind: 'Annual' },
};

const METHODS: { id: Method; name: string; sub: string; note?: string }[] = [
  { id: 'payhere', name: 'PayHere', sub: 'PayHere · Cards · eZ Cash · Alipay', note: 'Secure PayHere hosted checkout — also handles Alipay.' },
];

function useQueryPlan() {
  const loc = useLocation();
  return useMemo(() => {
    const q = new URLSearchParams(loc.search);
    return { plan: (q.get('plan') || 'gold_annual'), coupon: (q.get('coupon') || '').toUpperCase() };
  }, [loc.search]);
}

function fmt(n: number, cur = 'USD') {
  return cur === 'USD' ? `$${n.toFixed(2)}` : `${cur} ${n.toFixed(2)}`;
}

export default function Payments() {
  const { plan, coupon } = useQueryPlan();
  const meta = PLAN_META[plan] || PLAN_META.gold_annual;
  const loc = useLocation();
  const recompleteOrderId = useMemo(() => new URLSearchParams(loc.search).get('order_id') || '', [loc.search]);
  const recompleteToken = useMemo(() => new URLSearchParams(loc.search).get('token') || '', [loc.search]);
  const isRecomplete = useMemo(() => new URLSearchParams(loc.search).get('recomplete') === '1', [loc.search]);

  const [authed, setAuthed] = useState(API.loggedIn());
  const [method, setMethod] = useState<Method>('payhere');
  const [busy, setBusy] = useState(false);
  const [intent, setIntent] = useState<any | null>(null);
  const [couponPct, setCouponPct] = useState(0);
  const [couponMsg, setCouponMsg] = useState('');
  const [err, setErr] = useState('');
  // Phone number — carried through /payment/intent to the processor. Used for
  // Stripe billing_details, PayHere hosted form (was previously manual), and
  // for support to reach the user if the charge is disputed. Persist locally
  // so returning users don't retype it.
  const [phone, setPhone] = useState<string>(() => { try { return localStorage.getItem('vpnee_billing_phone') || ''; } catch { return ''; } });

  useEffect(() => { setAuthed(API.loggedIn()); }, []);

  // Validate the passed coupon once so we can price the receipt without a redirect.
  useEffect(() => {
    if (!coupon || !authed) return;
    (async () => {
      const r = await API.applyCoupon(coupon);
      if (r.success && r.data?.reward_type === 'discount') {
        setCouponPct(Number(r.data.discount_percentage) || 0);
        setCouponMsg(`Code ${coupon} · −${r.data.discount_percentage}% off`);
      }
    })();
  }, [coupon, authed]);

  // If recompleting a pending invoice (from Account → Complete payment), preload its receipt token and amount
  const [recompleteInv, setRecompleteInv] = useState<any>(null);
  // For recomplete: just load the invoice to show receipt, DON'T auto-mint new intent.
  // User must explicitly click "Complete payment" / "Pay now" to mint; otherwise new payments get hijacked.
  useEffect(() => {
    if (!isRecomplete || !recompleteOrderId || !authed) return;
    (async () => {
      const r = await API.invoices();
      const inv = (r.data || []).find((x: any) => x.order_id === recompleteOrderId);
      if (inv) {
        setRecompleteInv(inv);
        if (inv.receipt_token || inv.token) {
          setIntent({ url: '', token: inv.receipt_token || inv.token, order_id: inv.order_id, amount: parseFloat(inv.amount), currency: inv.currency });
        }
      }
    })();
  }, [isRecomplete, recompleteOrderId, authed]);

  const base = recompleteInv ? parseFloat(recompleteInv.amount || '0') : meta.price;
  const discount = !recompleteInv && couponPct > 0 ? +(base * (couponPct / 100)).toFixed(2) : 0;
  const total = recompleteInv ? base : +(base - discount).toFixed(2);
  const displayPlan = recompleteInv ? (recompleteInv.plan || plan).split('|')[0] : plan;
  const displayMeta = recompleteInv ? { label: displayPlan.replace('gold_','Gold ').replace('platinum_','Platinum '), price: base, kind: 'Recomplete' } : meta;
  const username = (() => { try { return JSON.parse(store.get('vpnee_user') || '{}').username || ''; } catch { return ''; } })();

  // Loose E.164-ish check — allow +, digits, spaces, dashes, parens; 7-15 digits.
  const phoneOk = (() => {
    const digits = phone.replace(/[^\d]/g, '');
    return digits.length >= 7 && digits.length <= 15;
  })();

  const createIntent = async () => {
    if (!phoneOk) { setErr('Enter a valid phone number so we can reach you about this order.'); return; }
    setBusy(true); setErr('');
    try { localStorage.setItem('vpnee_billing_phone', phone); } catch {}
    if (isRecomplete && recompleteOrderId) {
      const res: any = await API.repayIntent(recompleteOrderId, method, phone);
      setBusy(false);
      if (res?.error) { setErr(res.error); return; }
      if (!res?.token && !res?.client_secret) { setErr(res?.error || 'Could not prepare recompletion for this receipt.'); return; }
      setIntent({
        url: res.url || '',
        token: res.token,
        order_id: res.order_id || recompleteOrderId,
        amount: res.amount ?? total,
        currency: res.currency || 'USD',
        client_secret: res.client_secret,
        publishable_key: res.publishable_key,
      });
      return;
    }
    const res: any = await API.paymentIntent(plan, coupon, method, phone);
    setBusy(false);
    if (res?.error) { setErr(res.error); return; }
    if (!res || (!res.url && !res.client_secret)) { setErr(res?.error || 'Could not start checkout. Try again.'); return; }
    setIntent(res);
  };

  const goPay = () => {
    // If Stripe is available, stay on SPA — form already shown
    if (intent?.client_secret && intent?.publishable_key) return;
    if (intent?.url) window.location.href = intent.url;
  };

  if (!authed) {
    return (
      <Section chip="Checkout" title="Sign in to continue.">
        <div className="max-w-md">
          <p className="text-[color:var(--color-ink-dim)]">You need an account before we can create a secure checkout token.</p>
          <Link to={`/account?next=${encodeURIComponent('/payments' + (plan ? `?plan=${plan}${coupon ? `&coupon=${coupon}` : ''}` : ''))}`} className="btn btn-primary mt-4">Sign in →</Link>
        </div>
      </Section>
    );
  }

  return (
    <Section chip="Checkout" title={isRecomplete ? "Recomplete your payment." : "Complete your purchase."} subtitle={isRecomplete ? "Your previous payment was cancelled or pending — recomplete here with your receipt token." : "One-shot server-minted token · zero card details stored · you leave the site only for the processor."}>
      {isRecomplete && recompleteOrderId && (
        <div className="mb-6 rounded-2xl border border-[color:var(--color-brand)]/40 bg-[color:var(--color-brand)]/10 p-4 flex items-start gap-3">
          <span className="mt-0.5 w-6 h-6 rounded-full grid place-items-center bg-[color:var(--color-brand)]/25 text-[color:var(--color-brand-glow)] shrink-0">
            <IconRefresh width={14} height={14} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-bold">Recompleting order <span className="font-mono">{recompleteOrderId}</span></div>
            <div className="text-sm text-[color:var(--color-ink-dim)] mt-0.5">Receipt token: <span className="font-mono text-[color:var(--color-brand-glow)]">{recompleteToken ? recompleteToken.slice(0,16) : '—'}</span> — this token specifies which receipt you are paying for.</div>
            <div className="text-xs text-[color:var(--color-ink-faint)] mt-1">No PayHere — direct checkout with token. Your previous attempt was cancelled; no charge was made.</div>
          </div>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[1fr_360px] items-start">

        {/* ── LEFT — contact + method picker + trust ── */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="glass p-6 sm:p-8">

          {/* Step 1 — contact (phone) */}
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl grid place-items-center bg-[color:var(--color-brand)]/15 text-[color:var(--color-brand-glow)]">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.9.6 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.9.5 2.8.6a2 2 0 0 1 1.7 2z"/></svg>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)] font-bold">Step 1</div>
              <div className="h-title text-xl font-extrabold">Contact details</div>
            </div>
          </div>

          <label className="block mt-4">
            <span className="text-xs text-[color:var(--color-ink-dim)] font-semibold">Phone number</span>
            <input
              type="tel" inputMode="tel" autoComplete="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+94 71 234 5678"
              className={`mt-1 w-full rounded-xl bg-black/30 border px-4 py-3 text-sm outline-none focus:border-[color:var(--color-brand)] ${phone && !phoneOk ? 'border-red-500/60' : 'border-[color:var(--color-line)]'}`}
              aria-invalid={!!phone && !phoneOk}
            />
            <span className="text-[11px] text-[color:var(--color-ink-faint)] mt-1 block">Used only for order support and to reach you if the charge is disputed. Not shared.</span>
          </label>

          {/* Step 2 — method */}
          <div className="mt-7 flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl grid place-items-center bg-[color:var(--color-brand)]/15 text-[color:var(--color-brand-glow)]"><IconCreditCard width={18} height={18}/></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)] font-bold">Step 2</div>
              <div className="h-title text-xl font-extrabold">Choose payment method</div>
            </div>
          </div>

          <div className="mt-5 grid gap-3">
            {METHODS.map(m => {
              const active = method === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => { setMethod(m.id); setIntent(null); }}
                  className={`text-left rounded-2xl p-4 border transition flex items-center gap-4 ${active
                    ? 'bg-[color:var(--color-brand)]/10 border-[color:var(--color-brand)]/50 shadow-[0_10px_30px_-15px_rgba(232,71,106,0.6)]'
                    : 'bg-white/[0.03] border-[color:var(--color-line)] hover:border-[color:var(--color-brand)]/30'}`}
                >
                  <MethodBadge id={m.id} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold">{m.name}</div>
                    <div className="text-xs text-[color:var(--color-ink-dim)] mt-0.5">{m.sub}</div>
                    {m.note && <div className="text-[11px] text-[color:var(--color-ink-faint)] mt-1">{m.note}</div>}
                  </div>
                  <span className={`w-5 h-5 rounded-full border-2 grid place-items-center ${active ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand)]' : 'border-[color:var(--color-line-2)]'}`}>
                    {active && <IconCheck width={12} height={12} className="text-white" />}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex items-center gap-2 text-[11px] text-[color:var(--color-ink-faint)]">
            <IconShield width={13} height={13} />
            <span>Signed intent · single-use token · card details never touch our servers.</span>
          </div>

          {/* Intent panel — Stripe card form for direct card entry, or hosted
              redirect for Alipay / fallback. */}
          <AnimatePresence>
            {intent && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className="mt-6 overflow-hidden"
              >
                <div className="rounded-2xl border border-[color:var(--color-brand)]/30 bg-[color:var(--color-brand)]/8 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-[color:var(--color-ok)] animate-pulse" />
                    <div className="text-xs uppercase tracking-widest text-[color:var(--color-brand-glow)] font-bold">Checkout ready</div>
                  </div>
                  <div className="text-xs text-[color:var(--color-ink-dim)]">Order</div>
                  <div className="font-mono text-sm font-bold break-all">{intent.order_id || '—'}</div>
                  {intent.token && (
                    <>
                      <div className="text-xs text-[color:var(--color-ink-dim)] mt-2">Token · expires in 15 min</div>
                      <div className="font-mono text-[11px] break-all text-[color:var(--color-ink)]/90 bg-black/30 rounded-lg p-2 mt-1">{intent.token}</div>
                    </>
                  )}

                  {intent.payhere ? (
                    <PayHereForm data={intent} />
                  ) : (
                    <div className="mt-3 flex gap-2">
                      <button onClick={goPay} className="btn btn-primary flex-1 justify-center"><IconLock width={16} height={16}/> Continue to processor</button>
                      <button onClick={createIntent} className="btn btn-ghost" title="Regenerate" aria-label="Regenerate"><IconRefresh width={16} height={16}/></button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {err && <div className="mt-4 text-sm text-[color:var(--color-brand-glow)]">{err}</div>}

          {!intent && (
            <button onClick={createIntent} disabled={busy || !phoneOk} className="btn btn-primary w-full mt-6 justify-center disabled:opacity-50">
              {busy ? 'Securing checkout…' : !phoneOk ? 'Enter phone to continue' : <>Pay {fmt(total)} · {METHODS.find(m=>m.id===method)?.name}</>}
            </button>
          )}
        </motion.div>

        {/* ── RIGHT — receipt ── */}
        <motion.aside initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }} className="glass p-6 sm:p-7 lg:sticky lg:top-24">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--color-brand-glow)] font-bold">Receipt</div>
          <div className="h-title text-2xl font-extrabold mt-1">Order summary</div>
          <div className="text-xs text-[color:var(--color-ink-faint)] mt-1">for <span className="font-mono">{username || '—'}</span></div>

          <div className="mt-5 flex items-center gap-3 rounded-xl bg-white/[0.03] border border-[color:var(--color-line)] p-3">
            <div className="w-9 h-9 rounded-lg grid place-items-center bg-gradient-to-br from-[color:var(--color-brand)] to-[color:var(--color-brand-deep)] text-white text-xs font-black">{(isRecomplete ? displayMeta.label[0] : meta.label[0])}</div>
            <div className="flex-1 min-w-0">
              <div className="font-bold">Vpnee {isRecomplete ? displayMeta.label : meta.label}</div>
              <div className="text-xs text-[color:var(--color-ink-dim)]">{isRecomplete ? 'Recomplete · ' : ''}{isRecomplete ? displayMeta.kind : meta.kind} · REALITY + ML-KEM {isRecomplete && recompleteOrderId ? `· ${recompleteOrderId}` : ''}</div>
            </div>
            <div className="text-right text-sm font-mono font-bold">{fmt(isRecomplete ? base : base)}</div>
          </div>

          <dl className="mt-5 space-y-2 text-sm">
            <Row k="Subtotal" v={fmt(base)} />
            {discount > 0 && <Row k={couponMsg || `Coupon ${coupon}`} v={`− ${fmt(discount)}`} accent />}
            <Row k="Taxes" v="Included" muted />
            <div className="h-px bg-[color:var(--color-line)] my-2" />
            <Row k={<span className="text-base font-bold">Total</span>} v={<span className="text-lg font-extrabold text-[color:var(--color-brand-glow)]">{fmt(total)}</span>} />
          </dl>

          <div className="mt-4 text-[11px] text-[color:var(--color-ink-faint)] flex items-center gap-1.5">
            <IconLock width={12} height={12}/> Zero-log · cancel anytime from your account.
          </div>

          <Link to="/account" className="mt-5 flex items-center justify-center gap-1.5 text-xs text-center text-[color:var(--color-ink-dim)] hover:text-[color:var(--color-brand-glow)] transition"><IconArrowLeft width={12} height={12}/> Back to account</Link>
        </motion.aside>
      </div>
    </Section>
  );
}

/** Small pieces kept inline — nothing here is reused elsewhere. */
function Row({ k, v, accent = false, muted = false }: { k: React.ReactNode; v: React.ReactNode; accent?: boolean; muted?: boolean }) {
  return (
    <div className={`flex items-center justify-between ${muted ? 'text-[color:var(--color-ink-faint)]' : accent ? 'text-[color:var(--color-brand-glow)]' : 'text-[color:var(--color-ink-dim)]'}`}>
      <dt>{k}</dt><dd className="font-mono">{v}</dd>
    </div>
  );
}

/**
 * Loads Stripe.js from https://js.stripe.com/v3 on demand (no npm dep), mounts
 * a real card Element into a div, and calls `confirmCardPayment(client_secret)`
 * when the user hits Pay. Card details go direct from Stripe's iframe to
 * Stripe — never touch our server. Backend gets the outcome via webhook.
 */
declare global { interface Window { Stripe?: any; } }
let stripeLoader: Promise<void> | null = null;
function loadStripeJs(): Promise<void> {
  if (stripeLoader) return stripeLoader;
  stripeLoader = new Promise((resolve, reject) => {
    if (window.Stripe) return resolve();
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3'; s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Stripe.js'));
    document.head.appendChild(s);
  });
  return stripeLoader;
}

// kept for legacy — PayHere is default now, Stripe not used but kept for backward compat
// @ts-ignore
function StripeCardForm({ clientSecret, publishableKey, amount, phone, onSuccess }: {
  clientSecret: string; publishableKey: string; amount: number; phone?: string; onSuccess: () => void;
}) {
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const holder = useMemo(() => 'card-el-' + Math.random().toString(36).slice(2, 8), []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await loadStripeJs();
        if (!mounted || !window.Stripe) return;
        const stripe = window.Stripe(publishableKey);
        const elements = stripe.elements({ appearance: { theme: 'night', variables: { colorPrimary: '#e8476a', colorBackground: '#0a0508', colorText: '#fbf7fb', borderRadius: '10px' } } });
        const card = elements.create('card', { hidePostalCode: true });
        card.mount('#' + holder);
        card.on('change', (ev: any) => setErr(ev.error?.message || ''));
        // Store via closure since we can't set into useState-tuple.
        (window as any).__vpneeStripe = { stripe, card };
        setReady(true);
      } catch (e: any) {
        setErr(e?.message || 'Stripe failed to load');
      }
    })();
    return () => { mounted = false; try { (window as any).__vpneeStripe?.card?.destroy?.(); } catch {} };
  }, [holder, publishableKey]);

  const pay = async () => {
    const w = (window as any).__vpneeStripe;
    if (!w?.stripe || !w?.card) { setErr('Card form not ready'); return; }
    setBusy(true); setErr('');
    const { paymentIntent, error } = await w.stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: w.card, billing_details: phone ? { phone } : undefined },
    });
    setBusy(false);
    if (error) { setErr(error.message || 'Payment failed'); return; }
    if (paymentIntent?.status === 'succeeded') onSuccess();
    else setErr('Payment status: ' + (paymentIntent?.status || 'unknown'));
  };

  return (
    <div className="mt-3">
      <div id={holder} className="rounded-xl bg-black/30 border border-[color:var(--color-line)] p-3 min-h-[44px]" />
      {!ready && <div className="text-xs text-[color:var(--color-ink-faint)] mt-2">Loading secure card form…</div>}
      {err && <div className="text-sm text-[color:var(--color-brand-glow)] mt-2">{err}</div>}
      <button disabled={!ready || busy} onClick={pay} className="btn btn-primary w-full mt-3 justify-center disabled:opacity-50">
        <IconLock width={16} height={16}/> {busy ? 'Charging…' : `Pay $${amount.toFixed(2)} now`}
      </button>
      <div className="text-[11px] text-[color:var(--color-ink-faint)] mt-2 text-center">Card details go direct to Stripe (PCI-DSS SAQ A). We never see them.</div>
    </div>
  );
}

function PayHereForm({ data }: { data: any }) {
  const formRef = useMemo(() => 'ph-' + Math.random().toString(36).slice(2,7), []);
  useEffect(() => {
    // auto-submit handled by user click, not auto
  }, []);
  const submit = () => {
    const form = document.getElementById(formRef) as HTMLFormElement | null;
    if (form) form.submit();
  };
  return (
    <div className="mt-3">
      <form id={formRef} method="POST" action={data.payhere_url}>
        <input type="hidden" name="merchant_id" value={data.merchant_id} />
        <input type="hidden" name="return_url" value={data.return_url} />
        <input type="hidden" name="cancel_url" value={data.cancel_url} />
        <input type="hidden" name="notify_url" value={data.notify_url} />
        <input type="hidden" name="order_id" value={data.order_id} />
        <input type="hidden" name="items" value={data.items} />
        <input type="hidden" name="currency" value={data.currency} />
        <input type="hidden" name="amount" value={data.amount} />
        <input type="hidden" name="hash" value={data.hash} />
        <input type="hidden" name="first_name" value={data.first_name} />
        <input type="hidden" name="last_name" value={data.last_name} />
        <input type="hidden" name="email" value={data.email} />
        <input type="hidden" name="phone" value={data.phone} />
        <input type="hidden" name="address" value={data.address} />
        <input type="hidden" name="city" value={data.city} />
        <input type="hidden" name="country" value={data.country} />
      </form>
      <button onClick={submit} className="btn btn-primary w-full justify-center"><IconLock width={16} height={16}/> Pay via PayHere — ${Number(data.amount_num ?? data.amount).toFixed(2)}</button>
      <div className="text-[11px] text-[color:var(--color-ink-faint)] mt-2 text-center">You will be redirected to PayHere (also handles Alipay) — secure hosted checkout. Return to /account after payment.</div>
    </div>
  );
}

function MethodBadge({ id }: { id: string }) {
  const cls = 'w-11 h-11 shrink-0 rounded-xl grid place-items-center border';
  if (id === 'payhere') {
    return (
      <div className={`${cls} bg-[#2a0a14] border-[#5a1a2e] text-[#E8476A] font-black text-[10px]`}>PayHere</div>
    );
  }
  return (
    <div className={`${cls} bg-white/5 border-[color:var(--color-line)] text-[color:var(--color-brand-glow)]`}>
      <IconCreditCard width={20} height={20}/>
    </div>
  );
}
// keep legacy Stripe helpers referenced to avoid TS unused error (PayHere is default)
void StripeCardForm; void loadStripeJs; void stripeLoader;
